import { useMutation, useQuery } from "@apollo/client";
import type { FetchResult } from "@apollo/client";
import { AppliedFilters, Table, useModal } from "@fluidattacks/design";
import type { Row } from "@tanstack/react-table";
import _ from "lodash";
import { StrictMode, useCallback, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ActionButtons } from "./action-buttons";
import { AdditionModal } from "./addition-modal";
import { GET_ROOTS } from "./addition-modal/queries";
import type { IRootsData } from "./addition-modal/types";
import { useToeInputsPagination } from "./hooks/use-inputs-pagination";
import {
  GET_TOE_INPUTS,
  GET_TOE_INPUTS_COMPONENTS,
  TOE_INPUTS_FRAGMENT,
  UPDATE_TOE_INPUT,
} from "./queries";
import type {
  IGroupToeInputsProps,
  IToeInputAttr,
  IToeInputData,
  IToeInputEdge,
} from "./types";
import { formatToeInputData, getTableColumns, isEqualRootId } from "./utils";

import { useToEInputsFilters } from "features/toes-inputs/filters";
import { getFragmentData } from "gql/fragment-masking";
import type {
  CsvReportFiltersInput,
  GetToeInputsQueryVariables,
  ToeInputsFiltersInput,
  ToeInputsSortInput,
  UpdateToeInputMutation,
} from "gql/graphql";
import { useAuthz } from "hooks";
import { useTable } from "hooks/use-table";
import { getErrors } from "utils/helpers";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";
import { useTableServerSideStore } from "utils/tables";
import type { TStore } from "utils/zustand";

const GroupToeInputs: FC<IGroupToeInputsProps> = ({
  isInternal,
}): JSX.Element => {
  const { t } = useTranslation();
  const { canMutate, canResolve } = useAuthz();

  const canGetAttackedAt = canResolve("toe_input_attacked_at");
  const canGetAttackedBy = canResolve("toe_input_attacked_by");
  const canGetBePresentUntil = canResolve("toe_input_be_present_until");
  const canGetFirstAttackAt = canResolve("toe_input_first_attack_at");
  const canGetSeenFirstTimeBy = canResolve("toe_input_seen_first_time_by");
  const canUpdateToeInput = canMutate("update_toe_input");

  const { groupName } = useParams() as { groupName: string };
  const tableHelpers = useTableServerSideStore();
  const additionModalProps = useModal("addition-modal");
  const [isMarkingAsAttacked, setIsMarkingAsAttacked] = useState(false);
  const [filteredDataset, setFilteredDataset] = useState<IToeInputData[]>([]);
  const [backFilters, setBackFilters] = useState<ToeInputsFiltersInput>({});

  const getToeInputsVariables = useMemo(
    (): GetToeInputsQueryVariables => ({
      canGetAttackedAt,
      canGetAttackedBy,
      canGetBePresentUntil,
      canGetFirstAttackAt,
      canGetSeenFirstTimeBy,
      groupName,
    }),
    [
      canGetAttackedAt,
      canGetAttackedBy,
      canGetBePresentUntil,
      canGetFirstAttackAt,
      canGetSeenFirstTimeBy,
      groupName,
    ],
  );

  const { data: rootsData } = useQuery(GET_ROOTS, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't load roots", error);
      });
    },
    variables: { groupName },
  });

  const { data, handleNextPage, handleSearch, loading, refetch } =
    useToeInputsPagination({
      filters: backFilters,
      sortBy: tableHelpers.sortBy as ToeInputsSortInput[],
      variables: getToeInputsVariables,
    });

  const [handleUpdateToeInput, { client }] = useMutation(UPDATE_TOE_INPUT, {
    onCompleted: (): void => {
      void refetch();
    },
    onError: (errors): void => {
      errors.graphQLErrors.forEach((error): void => {
        switch (error.message) {
          case "Exception - The toe input is not present":
            msgError(t("group.toe.inputs.alerts.nonPresent"));
            break;
          case "Exception - The attack time must be between the previous attack and the current time":
            msgError(t("group.toe.inputs.alerts.invalidAttackedAt"));
            break;
          case "Exception - The toe input has been updated by another operation":
            msgError(t("group.toe.inputs.alerts.alreadyUpdate"));
            break;
          case "Exception - The environment has been excluded":
            msgError(t("group.toe.inputs.alerts.excludedUrlFromScope"));
            break;
          default:
            msgError(t("groupAlerts.errorTextsad"));
            Logger.warning("An error occurred updating the toe input", error);
        }
      });
    },
  });
  const hasActiveFilters = Object.values(backFilters).some((value): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;

    return true;
  });

  const size = data?.group.toeInputs.total;

  const { data: toeInputsComponentsData, loading: loadingComponents } =
    useQuery(GET_TOE_INPUTS_COMPONENTS, {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          msgError(t("groupAlerts.errorTextsad"));
          Logger.error("Couldn't load toe inputs components", error);
        });
      },
      variables: { groupName },
    });

  const toeInputs = useMemo((): IToeInputData[] => {
    const toeInputsEdges = data === undefined ? [] : data.group.toeInputs.edges;

    return toeInputsEdges.map(
      (edge): IToeInputData => formatToeInputData(edge.node as IToeInputAttr),
    );
  }, [data]);

  const toeInputsComponents = useMemo((): string[] => {
    return toeInputsComponentsData === undefined
      ? []
      : (
          toeInputsComponentsData as {
            group: { toeInputsComponents: string[] };
          }
        ).group.toeInputsComponents;
  }, [toeInputsComponentsData]);

  const { Filters, options, removeFilter } = useToEInputsFilters({
    groupName,
    setBackFilters,
    setFilteredDataset,
    toeInputs,
    toeInputsComponents,
  });
  const handleUpdateToeInputBePresent = useCallback(
    async (row: IToeInputData): Promise<void> => {
      const { rootId, component, entryPoint, bePresent } = row;
      const result = await handleUpdateToeInput({
        variables: {
          ...getToeInputsVariables,
          bePresent: !bePresent,
          component,
          entryPoint,
          groupName,
          hasRecentAttack: undefined,
          rootId,
          shouldGetNewToeInput: true,
        },
      });

      if (!_.isNil(result.data) && result.data.updateToeInput.success) {
        const updatedToeInput = result.data.updateToeInput
          .toeInput as IToeInputAttr;
        if (!_.isUndefined(updatedToeInput)) {
          if (data) {
            client.writeQuery({
              data: {
                ...data,
                group: {
                  ...data.group,
                  toeInputs: {
                    ...data.group.toeInputs,
                    edges: data.group.toeInputs.edges.map(
                      (value): IToeInputEdge => {
                        const node = getFragmentData(
                          TOE_INPUTS_FRAGMENT,
                          value.node,
                        );

                        if (
                          node.component === component &&
                          node.entryPoint === entryPoint &&
                          isEqualRootId(node.root, rootId)
                        ) {
                          return { node: updatedToeInput };
                        }

                        return { node: node as IToeInputAttr };
                      },
                    ),
                  },
                },
              },
              query: GET_TOE_INPUTS,
              variables: {
                ...getToeInputsVariables,
                ...backFilters,
                first: 150,
              },
            });
          }
        }
        msgSuccess(
          t("group.toe.inputs.alerts.updateInput"),
          t("groupAlerts.updatedTitle"),
        );
      }
    },
    [
      backFilters,
      client,
      data,
      getToeInputsVariables,
      groupName,
      handleUpdateToeInput,
      t,
    ],
  );

  const enabledRows = useCallback((row: Row<IToeInputData>): boolean => {
    return row.original.bePresent;
  }, []);

  const tableColumns = getTableColumns(
    isInternal,
    handleUpdateToeInputBePresent,
    {
      canGetAttackedAt,
      canGetAttackedBy,
      canGetBePresentUntil,
      canGetFirstAttackAt,
      canGetSeenFirstTimeBy,
      canUpdateToeInput,
    },
  );

  const { table } = useTable({
    columns: tableColumns,
    data: filteredDataset,
    fetchMore: handleNextPage,
    filters: tableHelpers as unknown as TStore<IToeInputData, string>,
    id: "toe-inputs-table",
    options: {
      actionsOptions: { onSearch: handleSearch },
      columnOptions: {
        hidden: [
          "attacked_by",
          "be_present",
          "be_present_until",
          "first_attack_at",
          "seen_first_time_by",
        ],
        order: ["root", "entry_point", "status", "seen_at", "component"],
        pinned: ["root"],
      },
      loading,
      rowOptions: {
        enableMultiSelection: isInternal && canUpdateToeInput,
        validator: enabledRows,
      },
    },
    total: data?.group.toeInputs.total ?? 0,
  });
  const { rowSelection } = table.getState();
  const { data: tableData } = table.options.meta ?? {};
  const selectedToeInputs: IToeInputData[] = Object.keys(rowSelection).map(
    (key): IToeInputData => tableData?.get(key) ?? ({} as IToeInputData),
  );

  const toggleAdd = useCallback((): void => {
    additionModalProps.open();
  }, [additionModalProps]);

  const handleOnMarkAsAttackedCompleted = useCallback(
    (result: FetchResult<UpdateToeInputMutation>): void => {
      if (!_.isNil(result.data) && result.data.updateToeInput.success) {
        msgSuccess(
          t("group.toe.inputs.alerts.markAsAttacked.success"),
          t("groupAlerts.updatedTitle"),
        );
        table.setRowSelection({});
      }
    },
    [table, t],
  );

  const handleMarkAsAttacked = useCallback(async (): Promise<void> => {
    const presentSelectedToeInputData = selectedToeInputs.filter(
      (toeInputData): boolean => toeInputData.bePresent,
    );
    setIsMarkingAsAttacked(true);
    const results = await Promise.all(
      presentSelectedToeInputData.map(
        async (toeInputData): Promise<FetchResult<UpdateToeInputMutation>> =>
          handleUpdateToeInput({
            variables: {
              ...getToeInputsVariables,
              bePresent: toeInputData.bePresent,
              component: toeInputData.component,
              entryPoint: toeInputData.entryPoint,
              groupName,
              hasRecentAttack: true,
              rootId: toeInputData.rootId,
              shouldGetNewToeInput: false,
            },
          }),
      ),
    );

    const errors = getErrors<UpdateToeInputMutation>(results);

    if (!_.isEmpty(results) && _.isEmpty(errors)) {
      handleOnMarkAsAttackedCompleted(results[0]);
    } else {
      void refetch();
    }
    setIsMarkingAsAttacked(false);
  }, [
    getToeInputsVariables,
    groupName,
    handleOnMarkAsAttackedCompleted,
    handleUpdateToeInput,
    refetch,
    selectedToeInputs,
  ]);

  const csvFilters: CsvReportFiltersInput = {
    toeInputsFilters: backFilters,
  };

  return (
    <StrictMode>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            {loadingComponents ? undefined : <Filters />}
            <Table.Columns table={table} />
            <ActionButtons
              areInputsSelected={selectedToeInputs.length > 0}
              filters={csvFilters}
              isAdding={additionModalProps.isOpen}
              isFiltered={hasActiveFilters && Boolean(size)}
              isInternal={isInternal}
              isMarkingAsAttacked={isMarkingAsAttacked}
              onAdd={toggleAdd}
              onMarkAsAttacked={handleMarkAsAttacked}
            />
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
      <AdditionModal
        groupName={groupName}
        modalRef={additionModalProps}
        refetchData={refetch}
        rootsData={
          rootsData === undefined ? undefined : (rootsData as IRootsData)
        }
      />
    </StrictMode>
  );
};

export { GroupToeInputs };
